/**
 * WhatsApp click-to-send opener — device-aware, no double-redirect.
 *
 * - Desktop  → https://web.whatsapp.com/send?phone=...&text=... (opens chat directly,
 *              skipping the wa.me "Continue to chat" interstitial).
 * - Mobile   → whatsapp://send?phone=...&text=... (deep-link into the installed app).
 *              Falls back to wa.me/<phone>?text=... after 1.5s if the deep link fails
 *              (e.g. WhatsApp not installed).
 *
 * Status returned to caller is always "Manual WhatsApp Opened" — the actual send
 * happens inside WhatsApp on the user's device.
 *
 * The opener also broadcasts a CustomEvent so a fallback modal anywhere on the page
 * can render copy-message UI if the popup is blocked.
 */

import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

let _openModeCache = null; // 'new_tab' | 'same_tab'

async function getOpenMode() {
  if (_openModeCache) return _openModeCache;
  try {
    const r = await axios.get(`${API}/company-settings`);
    _openModeCache = (r.data && r.data.whatsapp_open_mode) || "new_tab";
  } catch {
    _openModeCache = "new_tab";
  }
  return _openModeCache;
}

/** Reset cache after admin updates the open-mode setting. */
export function invalidateWhatsappOpenMode() {
  _openModeCache = null;
}

/**
 * Sanitise an Indian WhatsApp number to "91XXXXXXXXXX" (12 digits, no +/space/dash).
 * Returns "" if nothing usable.
 */
export function sanitiseIndianWhatsappNumber(raw) {
  if (!raw) return "";
  const digits = String(raw).replace(/\D/g, "");
  if (!digits) return "";
  if (digits.startsWith("91") && digits.length === 12) return digits;
  if (digits.length === 10) return "91" + digits;
  if (digits.startsWith("0") && digits.length === 11) return "91" + digits.slice(1);
  return digits; // last-resort
}

export function isMobileDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // Cover iOS, Android, Windows Phone — same heuristic WhatsApp itself uses.
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
}

/**
 * Build the right URL for the current device.
 * @returns {{ primary: string, fallback: string|null }}
 */
export function buildWhatsappUrls(rawPhone, message) {
  const phone = sanitiseIndianWhatsappNumber(rawPhone);
  const text = encodeURIComponent(message || "");
  const phoneSegment = phone ? `phone=${phone}&` : "";
  if (isMobileDevice()) {
    return {
      primary: `whatsapp://send?${phoneSegment}text=${text}`,
      fallback: phone ? `https://wa.me/${phone}?text=${text}` : `https://wa.me/?text=${text}`,
    };
  }
  // Desktop — go straight to web.whatsapp.com to skip wa.me's "Continue to chat" page.
  return {
    primary: phone
      ? `https://web.whatsapp.com/send?phone=${phone}&text=${text}`
      : `https://web.whatsapp.com/send?text=${text}`,
    fallback: null,
  };
}

/**
 * Open WhatsApp for the given recipient + message.
 *
 * @param {string} rawPhone Receiver phone (any format — sanitised internally)
 * @param {string} message Plain-text message (will be URL-encoded)
 * @param {object} [opts]
 * @param {boolean} [opts.openInSameTab] Override open-mode for this call
 * @returns {Promise<{phone: string, status: string, url: string}>}
 */
export async function openWhatsapp(rawPhone, message, opts = {}) {
  const phone = sanitiseIndianWhatsappNumber(rawPhone);
  if (!phone) {
    // Surface fallback modal so user can copy the message
    dispatchFallback({ phone: "", message: message || "" });
    return { phone: "", status: "missing_phone", url: "" };
  }

  const { primary, fallback } = buildWhatsappUrls(phone, message);
  const mode = opts.openInSameTab === true
    ? "same_tab"
    : opts.openInSameTab === false ? "new_tab" : await getOpenMode();
  const target = mode === "same_tab" ? "_self" : "_blank";

  if (isMobileDevice()) {
    // On mobile try the deep-link first; if WhatsApp isn't installed, browser stays on
    // the page, so after a short timeout we offer the wa.me fallback.
    let opened = false;
    const fallbackTimer = setTimeout(() => {
      if (!document.hidden && fallback) {
        // App didn't take over → switch to wa.me which works on mobile browsers too.
        const w = window.open(fallback, target);
        if (!w) dispatchFallback({ phone, message });
      }
    }, 1500);
    try {
      window.location.href = primary;
      opened = true;
    } catch (e) { /* ignore */ }
    if (!opened && fallback) {
      window.open(fallback, target);
    }
    // Clear timer on tab visibility change (means deep link worked & app opened)
    const onVis = () => { if (document.hidden) { clearTimeout(fallbackTimer); document.removeEventListener("visibilitychange", onVis); } };
    document.addEventListener("visibilitychange", onVis);
    return { phone, status: "Manual WhatsApp Opened", url: primary };
  }

  // Desktop
  const w = window.open(primary, target);
  if (!w || w.closed) {
    dispatchFallback({ phone, message, url: primary });
    return { phone, status: "popup_blocked", url: primary };
  }
  return { phone, status: "Manual WhatsApp Opened", url: primary };
}

function dispatchFallback(detail) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("whatsapp:fallback", { detail }));
}
